import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Descriptions } from 'antd';

import {
  dateToDDMMYYYYTTMM,
  dateToDDMMYYYYUniversal,
  no1000Seperator
} from '../../common/utils/formatter';
import AlertsLevelFraudComponent from './FraudAlertLevelComponent';
import AlertLevels from '../../common/components/AlertLevels';
import useAlertLevelConfigs from '../../common/hooks/useAlertLevelConfig';
import { floor, toLower } from '../../../util/lodashlike';

/**
 * DescriptionsComponent
 * @param props
 * @returns {*}
 * @constructor
 */
const DescriptionsComponent = (props) => {
  const itemStyle = { fontWeight: 500, fontSize: 11, color: '#31394D' };
  const {
    id,
    challengerid = '-',
    typeOfRun = '-',
    runTriggerDate = '-',
    runCompletionDate = '-',
    newAlerts = 0,
    noOfTypologies = '-',
    validationCompletion = 0,
    trueAlertCnt = '-',
    falseAlertCnt = '-',
    trainingStarted = '-',
    trainingCompleted = '-',
    trainingDateRange,
    predictionStarted = '-',
    predictionCompleted = '-',
    predictionDateRange,
    noOfCustomer = 0,
    noOfTransaction = 0,
    predictionRunId = '-',
    numTrueAlert = 0,
    numFalseAlert = 0,
    typologies = [],

    highLevelAlert = 0,
    goodLevelAlert = 0,
    lowLevelAlert = 0,

    highQualityAlert = 0,
    goodQualityAlert = 0,
    lowQualityAlert = 0,

    isFraudPrevention,
    actionCount = {},
    typologyActionList = [],
    alertLevels: alertLevelConfigs
  } = props;
  const { getLevelColor } = useAlertLevelConfigs(alertLevelConfigs);

  // FIN-12133: colour-code the Action by its configured severity's level colour
  // (action_severity -> alert_level_details), consistent with the Score Explanation.
  const getActionColor = useCallback(
    (action) =>
      getLevelColor(
        typologyActionList?.find(
          ({ displayName }) => toLower(displayName) === toLower(action)
        )?.actionSeverity
      ),
    [typologyActionList, getLevelColor]
  );

  const getFormatedLevel = useCallback(
    (levelAlert) => {
      const inPercent =
        (levelAlert / (highLevelAlert + goodLevelAlert + lowLevelAlert)) * 100;
      const formatedValue = inPercent ? `${floor(inPercent, 1)} %` : 0;

      return `${formatedValue} (${levelAlert}) ${' '}`;
    },
    [highLevelAlert, goodLevelAlert, lowLevelAlert]
  );

  const calcuLevelQualityPer = useCallback(
    (qualityAlert) => {
      const inPercent =
        (qualityAlert /
          (highQualityAlert + goodQualityAlert + lowQualityAlert)) *
        100;

      return inPercent ? `${floor(inPercent, 2)} %` : 0;
    },
    [highQualityAlert, goodQualityAlert, lowQualityAlert]
  );

  const parseFTStringToDate = (str) => {
    return (
      <>
        {dateToDDMMYYYYUniversal(str?.from)}
        &nbsp;-&nbsp;
        {dateToDDMMYYYYUniversal(str?.to)}
      </>
    );
  };

  const alertLevelData = useMemo(() => {
    return {
      High: getFormatedLevel(highLevelAlert),
      Good: getFormatedLevel(goodLevelAlert),
      Low: getFormatedLevel(lowLevelAlert)
    };
  }, [getFormatedLevel, highLevelAlert, goodLevelAlert, lowLevelAlert]);

  return (
    <div style={{ display: 'flex', border: '1px solid #D0D7E3', padding: 20 }}>
      {/* COLUMN 1 */}
      <Descriptions className="px-2" size={'small'} column={1}>
        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item contentStyle={itemStyle}>
          Type of Run : {typeOfRun}
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item labelStyle={itemStyle} label="Run Trigger Date ">
          {dateToDDMMYYYYTTMM(runTriggerDate)}
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item labelStyle={itemStyle} label="Run Completion Date ">
          {dateToDDMMYYYYTTMM(runCompletionDate)}
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item
          labelStyle={itemStyle}
          label={
            isFraudPrevention ? 'Number of Variants' : 'Number of Typologies'
          }>
          <span style={{ fontSize: '11px' }}>{typologies.length}</span>
        </Descriptions.Item>
      </Descriptions>

      {/* COLUMN 2 */}
      <Descriptions className="px-2" size={'small'} column={1}>
        {/* Challenger /Day 0/Day n */}
        <Descriptions.Item labelStyle={itemStyle} label="Alerts Generated">
          <small>{newAlerts}</small>
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item
          labelStyle={itemStyle}
          label="Alert Level"></Descriptions.Item>
        <Descriptions.Item contentStyle={itemStyle}>
          {isFraudPrevention ? (
            <AlertsLevelFraudComponent
              actionCount={actionCount}
              actionList={typologyActionList}
              getActionColor={getActionColor}
            />
          ) : (
            <AlertLevels data={alertLevelData} />
          )}
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item labelStyle={itemStyle} label="Validation Completion">
          <small>{validationCompletion}</small>
        </Descriptions.Item>

        {/* Challenger Day 0/Day n */}
        <Descriptions.Item
          contentStyle={{ ...itemStyle, color: getLevelColor('High') }}>
          High Quality Alerts: {calcuLevelQualityPer(highQualityAlert)} (
          {highQualityAlert} alerts)
        </Descriptions.Item>

        {/* Challenger Day 0/Day n */}
        <Descriptions.Item
          contentStyle={{ ...itemStyle, color: getLevelColor('Good') }}>
          Good Quality Alerts: {calcuLevelQualityPer(goodQualityAlert)} (
          {goodQualityAlert} alerts)
        </Descriptions.Item>

        {/* Challenger Day 0/Day n */}
        <Descriptions.Item
          contentStyle={{ ...itemStyle, color: getLevelColor('Low') }}>
          Low Quality Alerts: {calcuLevelQualityPer(lowQualityAlert)} (
          {lowQualityAlert} alerts)
        </Descriptions.Item>
      </Descriptions>

      {/* COLUMN 4*/}
      <Descriptions className="px-2" size={'small'} column={1}>
        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item
          labelStyle={itemStyle}
          label="Training Model Date Range">
          {trainingDateRange ? parseFTStringToDate(trainingDateRange) : '-'}
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item labelStyle={itemStyle} label="Prediction Date Range">
          {predictionDateRange ? parseFTStringToDate(predictionDateRange) : '-'}
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item labelStyle={itemStyle} label="Customers Processed">
          <span style={{ fontSize: '11px' }}>
            {no1000Seperator(noOfCustomer)}
          </span>
        </Descriptions.Item>

        {/* Challenger Day 0/Day n/Current Champion */}
        <Descriptions.Item
          labelStyle={itemStyle}
          label="Transactions Processed">
          <span style={{ fontSize: '11px' }}>
            {no1000Seperator(noOfTransaction)}
          </span>
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};

DescriptionsComponent.propTypes = {
  id: PropTypes.number,
  challengerid: PropTypes.number,
  typeOfRun: PropTypes.string,
  runTriggerDate: PropTypes.string,
  newAlerts: PropTypes.number,
  runCompletionDate: PropTypes.string,
  noOfTypologies: PropTypes.number,
  validationCompletion: PropTypes.number,
  trueAlertCnt: PropTypes.number,
  falseAlertCnt: PropTypes.number,
  trainingStarted: PropTypes.string,
  trainingCompleted: PropTypes.string,
  trainingDateRange: PropTypes.string,
  predictionStarted: PropTypes.string,
  predictionCompleted: PropTypes.string,
  predictionDateRange: PropTypes.string,
  noOfCustomer: PropTypes.number,
  noOfTransaction: PropTypes.number,
  predictionRunId: PropTypes.number,
  typologies: PropTypes.array,
  flag: PropTypes.oneOf(['challenger']),
  alertLevels: PropTypes.array
};

export default DescriptionsComponent;
